import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import wzData from '../data/wz2008.json';

interface WzRawEntry {
  code: string;
  title: string;
  keywords?: string[];
}

/** Grouped entry: 4-digit WZ code (e.g. "5610") with combined title */
interface WzEntry {
  code: string;       // "5610"
  codeDotted: string; // "56.10"
  title: string;      // "Restaurants, Imbissstuben, Cafés, Eissalons"
  keywords: string[];
}

interface WzSearchProps {
  value: string;
  onChange: (value: string) => void;
  darkMode: boolean;
  placeholder?: string;
  error?: boolean;
}

const MAX_RESULTS = 8;

/** Group raw XX.XX.X entries into 4-digit (XXXX) groups with combined titles */
function buildGroupedEntries(raw: WzRawEntry[]): WzEntry[] {
  const groups = new Map<string, { codeDotted: string; titles: string[]; keywords: string[] }>();

  for (const entry of raw) {
    // "56.10.1" → group key "56.10"
    const parts = entry.code.split('.');
    const groupKey = `${parts[0]}.${parts[1]}`;

    if (!groups.has(groupKey)) {
      groups.set(groupKey, { codeDotted: groupKey, titles: [], keywords: [] });
    }
    const g = groups.get(groupKey)!;
    // Avoid duplicate titles (e.g. when sub has same name as parent concept)
    if (!g.titles.some(t => t.toLowerCase() === entry.title.toLowerCase())) {
      g.titles.push(entry.title);
    }
    if (entry.keywords) {
      for (const kw of entry.keywords) {
        if (!g.keywords.includes(kw)) g.keywords.push(kw);
      }
    }
  }

  return Array.from(groups.entries()).map(([dotted, g]) => ({
    code: dotted.replace(/\./g, ''),  // "56.10" → "5610"
    codeDotted: dotted,                // "56.10"
    title: g.titles.join(', '),
    keywords: g.keywords,
  }));
}

const WZ_ENTRIES = buildGroupedEntries(wzData as WzRawEntry[]);

function formatEntry(entry: WzEntry) {
  return `${entry.code} – ${entry.title}`;
}

function findEntryByCode(code: string) {
  // Support lookup by "5610", "56.10", or old format "56.10.1"
  const normalized = code.replace(/\./g, '');
  // Try 4-digit match first
  const exact = WZ_ENTRIES.find(e => e.code === normalized || e.code === code || e.codeDotted === code);
  if (exact) return exact;
  // Old format "56.10.1" → try group "5610"
  const groupCode = normalized.slice(0, 4);
  return WZ_ENTRIES.find(e => e.code === groupCode) || null;
}

export function WzSearch({
  value,
  onChange,
  darkMode,
  placeholder = 'WZ 2008 suchen (Code oder Tätigkeit)…',
  error = false,
}: WzSearchProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [selectedEntry, setSelectedEntry] = useState<WzEntry | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (!value) {
      setSelectedEntry(null);
      setQuery('');
      return;
    }

    // Unterstütze sowohl altes Format (nur Code: "10.71.0") als auch neues ("10.71.0 – Herstellung von Backwaren")
    const codeOnly = value.split(' – ')[0].trim();
    const entry = findEntryByCode(codeOnly);
    if (entry) {
      setSelectedEntry(entry);
      setQuery(formatEntry(entry));
    } else {
      // Falls Alt-Daten oder ungültiger Wert vorhanden sind, direkt anzeigen
      setSelectedEntry(null);
      setQuery(value);
    }
  }, [value]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 1) return [];

    const terms = q.split(/\s+/).filter(Boolean);
    const scored: { entry: WzEntry; score: number }[] = [];

    for (const entry of WZ_ENTRIES) {
      const codeLower = entry.code.toLowerCase();
      const codeDottedLower = entry.codeDotted.toLowerCase();
      const titleLower = entry.title.toLowerCase();
      const keywordsLower = (entry.keywords || []).map(kw => kw.toLowerCase());

      let score = 0;
      let allMatch = true;

      for (const term of terms) {
        let termFound = false;

        // Match against both "5610" and "56.10"
        if (codeLower.startsWith(term) || codeDottedLower.startsWith(term)) {
          score += 100;
          termFound = true;
        } else if (codeLower.includes(term) || codeDottedLower.includes(term)) {
          score += 80;
          termFound = true;
        }

        if (titleLower.includes(term)) {
          score += titleLower.startsWith(term) ? 60 : 40;
          termFound = true;
        }

        for (const kw of keywordsLower) {
          if (kw.includes(term)) {
            score += kw.startsWith(term) ? 25 : 20;
            termFound = true;
            break;
          }
        }

        if (!termFound) {
          allMatch = false;
          break;
        }
      }

      if (allMatch && score > 0) {
        scored.push({ entry, score });
      }
    }

    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.entry.code.localeCompare(b.entry.code, 'de');
    });

    return scored.slice(0, MAX_RESULTS).map(item => item.entry);
  }, [query]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);

        // BAFA-safer Verhalten: freie Eingabe nicht stehen lassen.
        if (!selectedEntry) {
          setQuery('');
          onChange('');
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedEntry, onChange]);

  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightIndex] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [isOpen]);

  const selectEntry = useCallback(
    (entry: WzEntry) => {
      setSelectedEntry(entry);
      setQuery(formatEntry(entry));
      onChange(formatEntry(entry)); // gespeichert wird Code + Name, z.B. "10.71.0 – Herstellung von Backwaren"
      setIsOpen(false);
      setHighlightIndex(-1);
    },
    [onChange]
  );

  const clearSelection = useCallback(() => {
    setSelectedEntry(null);
    setQuery('');
    onChange('');
    setIsOpen(false);
    setHighlightIndex(-1);
    inputRef.current?.focus();
  }, [onChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setSelectedEntry(null);
    setIsOpen(val.trim().length >= 1);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' && query.trim().length >= 1 && results.length > 0) {
        setIsOpen(true);
        setHighlightIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (results.length > 0) {
          setHighlightIndex(prev => (prev + 1) % results.length);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (results.length > 0) {
          setHighlightIndex(prev => (prev <= 0 ? results.length - 1 : prev - 1));
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < results.length) {
          selectEntry(results[highlightIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightIndex(-1);
        if (!selectedEntry) {
          setQuery('');
          onChange('');
        }
        break;
    }
  };

  const handleBlur = () => {
    // kleiner Delay, damit onClick auf Dropdown-Eintrag noch greift
    window.setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        if (!selectedEntry) {
          setQuery('');
          onChange('');
        }
      }
    }, 120);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />

        <input
          translate="no"
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            if (!selectedEntry && query.trim().length >= 1) {
              setIsOpen(true);
            }
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className={`w-full pl-9 pr-9 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm ${
            error
              ? 'border-red-500'
              : `${darkMode ? 'border-slate-600' : 'border-slate-300'}`
          } ${
            darkMode
              ? 'bg-slate-700 text-white placeholder:text-slate-500'
              : 'bg-white text-slate-900 placeholder:text-slate-400'
          }`}
        />

        {selectedEntry && (
          <button
            type="button"
            onClick={clearSelection}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors cursor-pointer ${
              darkMode ? 'hover:bg-slate-600 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
            }`}
            aria-label="Auswahl löschen"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {isOpen && (
        <ul
          translate="no"
          ref={listRef}
          style={dropdownStyle}
          className={`rounded-lg shadow-lg border z-[9999] max-h-64 overflow-y-auto ${
            darkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'
          }`}
        >
          {results.length > 0 ? (
            results.map((entry, i) => (
              <li
                key={entry.code}
                onMouseDown={e => e.preventDefault()}
                onClick={() => selectEntry(entry)}
                onMouseEnter={() => setHighlightIndex(i)}
                className={`px-3 py-2.5 cursor-pointer text-sm transition-colors ${
                  i === highlightIndex
                    ? darkMode
                      ? 'bg-purple-600/30 text-white'
                      : 'bg-purple-50 text-slate-900'
                    : darkMode
                      ? 'text-slate-200 hover:bg-slate-600'
                      : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className={darkMode ? 'font-medium text-purple-300' : 'font-medium text-purple-700'}>
                  {entry.code}
                </span>
                <span className={darkMode ? 'text-slate-400' : 'text-slate-400'}> – </span>
                <span>{entry.title}</span>
              </li>
            ))
          ) : (
            <li className={`px-3 py-3 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Keine passenden WZ-2008-Einträge gefunden
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
